import React, { useState } from 'react';
import {
  Box,
  Divider,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography
} from '@mui/material';
import TuneRoundedIcon from '@mui/icons-material/TuneRounded';
import TranslateRoundedIcon from '@mui/icons-material/TranslateRounded';

const languages = [
  { key: 'BG', label: 'Български' },
  { key: 'EN', label: 'English' },
  { key: 'RU', label: 'Русский' }
];

const fieldError = (name, errors, touched) => ({
  error: Boolean(touched[name] && errors[name]),
  helperText: touched[name] && errors[name] ? errors[name] : ' '
});

function SelectField({ name, label, value, options, errors, touched, handleChange, handleBlur }) {
  const hasError = Boolean(touched[name] && errors[name]);
  return (
    <FormControl fullWidth error={hasError}>
      <InputLabel>{label}</InputLabel>
      <Select name={name} label={label} value={value} onChange={handleChange} onBlur={handleBlur}>
        {options.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </Select>
      <FormHelperText>{hasError ? errors[name] : ' '}</FormHelperText>
    </FormControl>
  );
}

export default function ProductFormFields({
  values,
  errors,
  touched,
  handleChange,
  handleBlur,
  categories,
  subcategories,
  productTypes,
  materials,
  positionField,
  positionLabel
}) {
  const [activeLanguage, setActiveLanguage] = useState('BG');
  const currentLanguage = languages.find((language) => language.key === activeLanguage);

  return (
    <Stack spacing={4}>
      <Box>
        <Stack direction="row" alignItems="center" gap={1} mb={2.5}>
          <TuneRoundedIcon color="primary" fontSize="small" />
          <Box>
            <Typography variant="h6">Основна информация</Typography>
            <Typography variant="body2" color="text.secondary">
              Заглавие, статус, код, позиция и класификация на продукта.
            </Typography>
          </Box>
        </Stack>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              name="nameBG"
              label="Заглавие на продукта"
              value={values.nameBG}
              onChange={handleChange}
              onBlur={handleBlur}
              {...fieldError('nameBG', errors, touched)}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <SelectField
              name="status"
              label="Статус"
              value={values.status}
              options={[
                { value: 'published', label: 'Публикуван' },
                { value: 'draft', label: 'Чернова' }
              ]}
              errors={errors}
              touched={touched}
              handleChange={handleChange}
              handleBlur={handleBlur}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              name="code"
              label="Код на продукта"
              placeholder="Напр. R-345"
              value={values.code}
              onChange={handleChange}
              onBlur={handleBlur}
              {...fieldError('code', errors, touched)}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              name={positionField}
              label={positionLabel}
              type="number"
              value={values[positionField]}
              onChange={handleChange}
              onBlur={handleBlur}
              {...fieldError(positionField, errors, touched)}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <SelectField
              name="category"
              label="Категория"
              value={values.category}
              options={categories.map((item) => ({ value: item.id, label: item.name_bg }))}
              errors={errors}
              touched={touched}
              handleChange={handleChange}
              handleBlur={handleBlur}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <SelectField
              name="subCategory"
              label="Подкатегория"
              value={values.subCategory}
              options={subcategories.map((item) => ({ value: item.id, label: item.name_bg }))}
              errors={errors}
              touched={touched}
              handleChange={handleChange}
              handleBlur={handleBlur}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <SelectField
              name="productTypeAttribute"
              label="Тип продукт"
              value={values.productTypeAttribute}
              options={productTypes.map((item) => ({ value: item.id, label: item.name_bg }))}
              errors={errors}
              touched={touched}
              handleChange={handleChange}
              handleBlur={handleBlur}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <SelectField
              name="materialAttribute"
              label="Материал"
              value={values.materialAttribute}
              options={materials.map((item) => ({ value: item.id, label: item.name_bg }))}
              errors={errors}
              touched={touched}
              handleChange={handleChange}
              handleBlur={handleBlur}
            />
          </Grid>
        </Grid>
      </Box>

      <Divider />

      <Box>
        <Stack direction="row" alignItems="center" gap={1} mb={2}>
          <TranslateRoundedIcon color="primary" fontSize="small" />
          <Box>
            <Typography variant="h6">Съдържание</Typography>
            <Typography variant="body2" color="text.secondary">
              Заглавие, кратко и пълно описание на всеки език.
            </Typography>
          </Box>
        </Stack>
        <Tabs
          value={activeLanguage}
          onChange={(_event, value) => setActiveLanguage(value)}
          variant="scrollable"
          sx={{ borderBottom: '1px solid', borderColor: 'divider', mb: 3 }}
        >
          {languages.map((language) => (
            <Tab key={language.key} value={language.key} label={language.label} />
          ))}
        </Tabs>

        <Stack spacing={1}>
          {currentLanguage.key !== 'BG' && (
            <TextField
              fullWidth
              name={`name${currentLanguage.key}`}
              label={`Заглавие · ${currentLanguage.label}`}
              value={values[`name${currentLanguage.key}`]}
              onChange={handleChange}
              onBlur={handleBlur}
              {...fieldError(`name${currentLanguage.key}`, errors, touched)}
            />
          )}
          <TextField
            fullWidth
            multiline
            minRows={3}
            name={`summary${currentLanguage.key}`}
            label={`Кратко описание · ${currentLanguage.label}`}
            value={values[`summary${currentLanguage.key}`]}
            onChange={handleChange}
            onBlur={handleBlur}
            {...fieldError(`summary${currentLanguage.key}`, errors, touched)}
          />
          <TextField
            fullWidth
            multiline
            minRows={7}
            name={`description${currentLanguage.key}`}
            label={`Пълно описание · ${currentLanguage.label}`}
            value={values[`description${currentLanguage.key}`]}
            onChange={handleChange}
            onBlur={handleBlur}
            {...fieldError(`description${currentLanguage.key}`, errors, touched)}
          />
        </Stack>
        <Typography variant="caption" color="text.secondary">
          Нов ред в краткото и пълното описание се отбелязва с точка и запетая (;).
        </Typography>
      </Box>
    </Stack>
  );
}
